/**
 * Demo locale hook — reads `?lang` from the URL once at mount and
 * returns "ar" / "pt" / "en". Drives both the next-intl stub bundle
 * selection and the `<html dir lang>` toggle in App.tsx.
 *
 * Per-tab persistence is handled by the URL itself; no cookie, no
 * localStorage. Visiting `/demo?lang=ar` puts the entire SPA into
 * Arabic + RTL; `/demo?lang=pt` swaps to Brazilian Portuguese in LTR;
 * visiting without the param (or with `?lang=en`) is the canonical
 * English path. Presenter can toggle live by editing the URL.
 *
 * Supported locales today: en (default) + ar + pt. Anything else falls
 * back to en — keeps the bundle lookup deterministic.
 */

export type DemoLocale = "en" | "ar" | "pt";

export function readDemoLocale(): DemoLocale {
  if (typeof window === "undefined") return "en";
  const params = new URLSearchParams(window.location.search);
  const raw = params.get("lang");
  if (raw === "ar") return "ar";
  if (raw === "pt") return "pt";
  return "en";
}

let cached: DemoLocale | null = null;

/**
 * Singleton locale read — first call freezes the locale for the page
 * lifetime. The Vite next-intl stub calls this in render scope, so we
 * memoize to keep `useTranslations()` referentially stable across the
 * tree (otherwise every render swaps the function identity and breaks
 * memoized children).
 */
export function useDemoLocale(): DemoLocale {
  if (cached === null) cached = readDemoLocale();
  return cached;
}

/** Test seam — only used by unit specs to reset between cases. */
export function __resetDemoLocaleCache() {
  cached = null;
}

/**
 * Sets `<html dir lang>` synchronously on first load. Called at the
 * application root so every route (Marketing / AuthEntry / Demo /
 * AudienceRole) gets the right document direction before paint.
 *
 * Runs once at module-eval time rather than inside an effect so the
 * initial paint is already RTL for Arabic — no flash of LTR before the
 * effect fires.
 */
export function applyDocumentLocale(): void {
  if (typeof document === "undefined") return;
  const locale = readDemoLocale();
  const html = document.documentElement;
  html.dir  = locale === "ar" ? "rtl" : "ltr";
  html.lang = locale;
}
